import React, { FC, useCallback, useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import clsx from 'clsx';
import { useRecoilState } from 'recoil';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './SettingsSideBar.scss';
import { settingsSidebarStateAtom } from '../../../data-access';
import {
  settingsSections,
  SettingsSectionItemComponent,
  SETTINGS_SUB_SECTION_PARENT_MAP,
} from '../constants';

interface SettingsSideBarProps {
  className?: string;
}

interface SectionLeaf {
  key: string;
  title: string;
  titleKey: string;
}

const getImmediateSections = (item: SettingsSectionItemComponent): SectionLeaf[] => {
  if (!item.section?.length) {
    return [];
  }

  return item.section.map((sectionItem) => ({
    key: sectionItem.key,
    title: sectionItem.title,
    titleKey: sectionItem.titleKey,
  }));
};

const SettingsSideBar: FC<SettingsSideBarProps> = ({ className }) => {
  const { t } = useTranslation();
  const [settingsSidebarState, setSettingsSidebarState] = useRecoilState(settingsSidebarStateAtom);
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const selectedHashSectionKey = useMemo(
    () => decodeURIComponent(hash.replace('#', '').trim()),
    [hash]
  );

  const expandedParentKeys = useMemo(
    () => new Set(settingsSidebarState.expandedParentKeys),
    [settingsSidebarState.expandedParentKeys]
  );

  const handleSubParentClick = useCallback(
    (parentKey: string, shouldExpand: boolean) => {
      setSettingsSidebarState((prev) => ({
        ...prev,
        selectedParentKey: parentKey,
        expandedParentKeys: shouldExpand ? [parentKey] : [],
      }));
      navigate({ pathname, search, hash: parentKey });
    },
    [navigate, pathname, search, setSettingsSidebarState]
  );

  const handleSectionClick = useCallback(
    (parentKey: string, sectionKey: string) => {
      setSettingsSidebarState((prev) => ({
        ...prev,
        selectedParentKey: parentKey,
        expandedParentKeys: [parentKey],
      }));
      navigate({ pathname, search, hash: sectionKey });
    },
    [navigate, pathname, search, setSettingsSidebarState]
  );

  return (
    <aside className={clsx('settings-side-bar', className)} aria-label={t('settings.aria.sidebar')}>
      {settingsSections.map((topParent) => (
        <section key={topParent.key} className="settings-side-bar__section">
          <Typography variant="body1" className="settings-side-bar__section-title">
            {t(topParent.titleKey)}
          </Typography>
          <div className="settings-side-bar__section-items">
            {topParent.components.map((subParent) => {
              const sections = getImmediateSections(subParent);
              const hasSections = sections.length > 0;
              const isExpanded = expandedParentKeys.has(subParent.key);
              const isSelectedParent = settingsSidebarState.selectedParentKey === subParent.key;
              const Icon = subParent.icon;

              if (!hasSections) {
                return (
                  <button
                    key={subParent.key}
                    type="button"
                    className={clsx(
                      'settings-side-bar__item',
                      'settings-side-bar__item--button',
                      isSelectedParent && 'settings-side-bar__item--selected'
                    )}
                    onClick={() => handleSubParentClick(subParent.key, false)}
                  >
                    <div className="settings-side-bar__item-left">
                      {Icon && <Icon className="settings-side-bar__item-icon" />}
                      <Typography
                        variant="body2"
                        className={clsx(
                          'settings-side-bar__item-title',
                          isSelectedParent && 'settings-side-bar__item-title--selected'
                        )}
                      >
                        {t(subParent.titleKey)}
                      </Typography>
                    </div>
                  </button>
                );
              }

              return (
                <Accordion
                  key={subParent.key}
                  disableGutters
                  elevation={0}
                  expanded={isExpanded}
                  onChange={(_, expanded) => handleSubParentClick(subParent.key, expanded)}
                  className="settings-side-bar__accordion"
                >
                  <AccordionSummary
                    className={clsx(
                      'settings-side-bar__item',
                      isSelectedParent && 'settings-side-bar__item--selected'
                    )}
                  >
                    <div className="settings-side-bar__item-left">
                      {Icon && <Icon className="settings-side-bar__item-icon" />}
                      <Typography
                        variant="body2"
                        className={clsx(
                          'settings-side-bar__item-title',
                          isSelectedParent && 'settings-side-bar__item-title--selected'
                        )}
                      >
                        {t(subParent.titleKey)}
                      </Typography>
                    </div>
                  </AccordionSummary>

                  <AccordionDetails className="settings-side-bar__sub-sections">
                    {sections.map((section) => (
                      <button
                        key={section.key}
                        type="button"
                        className={clsx(
                          'settings-side-bar__sub-section-item',
                          selectedHashSectionKey === section.key &&
                            SETTINGS_SUB_SECTION_PARENT_MAP[section.key] === subParent.key &&
                            'settings-side-bar__sub-section-item--selected'
                        )}
                        onClick={() => handleSectionClick(subParent.key, section.key)}
                      >
                        <Typography
                          variant="body2"
                          className={clsx(
                            'settings-side-bar__sub-section-item-title',
                            selectedHashSectionKey === section.key &&
                              SETTINGS_SUB_SECTION_PARENT_MAP[section.key] === subParent.key &&
                              'settings-side-bar__sub-section-item-title--selected'
                          )}
                        >
                          {t(section.titleKey)}
                        </Typography>
                      </button>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        </section>
      ))}
    </aside>
  );
};

export default SettingsSideBar;
